import { Role } from '@lucidtech/las-sdk-browser';
import { memo, useCallback } from 'react';

import { merge } from '@/components';
import { Button, Card, Flex, Heading, IconProps, Save, Text, useComponentStack } from '@/components/core';
import { ManagedRole } from '@/constants';
import { toRoleId, useGetRole, useListRoles } from '@/hooks/api';

import { AdminRoleIcon, CustomRoleIcon, DeveloperRoleIcon, ReviewerRoleIcon } from '../icons';
import { AddOrEditRole } from './AddOrEditRole';

type RoleBadgeProps = {
  roleId?: string;
};

const IRoleBadge = ({ roleId }: RoleBadgeProps) => {
  const { data: role } = useGetRole(toRoleId(roleId));
  if (!role) return null;

  let style = '';
  let textStyle = '';
  switch (role.roleId) {
    case ManagedRole.Admin:
      style = 'bg-success-25 border-success-100';
      textStyle = '!text-success-700';
      break;
    case ManagedRole.Developer:
    case ManagedRole.Member:
      style = 'bg-cyan-25 border-cyan-100';
      textStyle = '!text-cyan-700';
      break;
    case ManagedRole.Reviewer:
    case ManagedRole.Validator:
      style = 'bg-warning-25 border-warning-200';
      textStyle = '!text-warning-700';
      break;
    default:
      style = 'bg-purple-25 border-purple-100';
      textStyle = '!text-purple-700';
  }

  return (
    <div className={merge('gap-xs flex items-center justify-center rounded-sm border px-1 py-0.5', style)}>
      <RoleIcon roleId={roleId} className="!size-3" />
      <Text size="xs" className={merge('truncate', textStyle)} slot="description">
        {role?.name}
      </Text>
    </div>
  );
};

export const RoleBadge = memo(IRoleBadge) as typeof IRoleBadge;

type RoleIconProps = RoleBadgeProps & Partial<IconProps>;
const IRoleIcon = ({ roleId, ...rest }: RoleIconProps) => {
  const { data: role } = useGetRole(toRoleId(roleId));
  if (!role) return null;

  switch (role.roleId) {
    case ManagedRole.Admin:
      return <AdminRoleIcon {...rest} />;
    case ManagedRole.Developer:
    case ManagedRole.Member:
      return <DeveloperRoleIcon {...rest} />;
    case ManagedRole.Reviewer:
    case ManagedRole.Validator:
      return <ReviewerRoleIcon {...rest} />;
  }

  return <CustomRoleIcon {...rest} />;
};

export const RoleIcon = memo(IRoleIcon) as typeof IRoleIcon;

export type RolesTableProps = object;

// eslint-disable-next-line no-empty-pattern
const IRolesTable = ({}: RolesTableProps) => {
  const { data: roles, isLoading } = useListRoles();
  const { push, pop } = useComponentStack();

  const onOpenRole = useCallback(
    (role: Role) => {
      push(<AddOrEditRole roleId={role?.roleId} close={pop} />);
    },
    [pop, push]
  );

  const sortedRoles = roles?.data.toSorted((lhs, rhs) => lhs.createdTime.getTime() - rhs.createdTime.getTime());

  return isLoading ? (
    <Save message="Loading roles ... " />
  ) : (
    <Flex variant="plain" col className="gap-sm w-full overflow-auto">
      {sortedRoles?.map((role) => (
        <Card
          col={false}
          className="p-md gap-sm cursor-pointer items-center bg-white hover:border-gray-200 hover:bg-gray-50"
          onClick={() => onOpenRole(role)}
        >
          <RoleIcon roleId={role.roleId} />

          <Flex col className="grow">
            <Heading size="sm">{role?.name}</Heading>
            <Text size="xs" variant="tertiary">
              {role?.description}
            </Text>
          </Flex>

          <Button variant="secondary" onPress={() => onOpenRole(role)} className="group/hover:border-5">
            <Text>Edit</Text>
          </Button>
        </Card>
      ))}
    </Flex>
  );
};

export const RolesTable = memo(IRolesTable) as typeof IRolesTable;
